import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, checkPermission, UserRole } from '../types';
import apiService from '../services/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  hasPermission: (requiredRole: UserRole) => boolean;
  checkAuth: () => Promise<void>;
  loginWithAzure: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: true,
      isAuthenticated: false,

      setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),
      setLoading: (loading: boolean) => set({ loading }),

      hasPermission: (requiredRole: UserRole) => {
        const { user } = get();
        if (!user) return false;
        return checkPermission(user.roles, requiredRole);
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token');
        
        if (!token) {
          set({ loading: false, user: null, isAuthenticated: false });
          return;
        }

        // Try API authentication
        try {
          const userData = await apiService.getCurrentUser();
          // Only consider the session authenticated if we actually got a user
          // back. Some backends resolve getCurrentUser() with an empty/falsy
          // body, which previously left isAuthenticated=true while user=null —
          // that inconsistent state renders the "Authentication Required" gate
          // instead of redirecting to the login page.
          if (!userData) {
            throw new Error('No user returned from getCurrentUser');
          }
          set({ user: userData, isAuthenticated: true, loading: false });
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expires_at');
          set({ user: null, isAuthenticated: false, loading: false });
        }
      },

      loginWithAzure: async () => {
        try {
          const { auth_url } = await apiService.getAuthUrl();
          window.location.href = auth_url;
        } catch (error) {
          console.error('Azure login failed:', error);
          throw error;
        }
      },

      logout: () => {
        // Clear local storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expires_at');
        set({ user: null, isAuthenticated: false });
        
        // Redirect to Azure AD logout endpoint
        const tenantId = '8c79acb0-9153-429c-b4e0-30a003e8e11f';
        const postLogoutRedirectUri = encodeURIComponent(window.location.origin + '/auth/login');
        const azureLogoutUrl = `https://login.microsoftonline.us/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
        
        window.location.href = azureLogoutUrl;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state: AuthState) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
